import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Modal, Typography, Empty, Card, Row, Col, Statistic, Table, Button, Space, Spin } from 'antd';
import { TableOutlined, BarChartOutlined } from '@ant-design/icons';
import { Slot } from '../../../Modules/Slot/Model/Slot';
import dayjs from 'dayjs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppDispatch, useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { fetchSlotRankHistory } from '../../../Modules/Slot/Presentation/actions';

const { Title, Text } = Typography;

interface Props {
  visible: boolean;
  slot: Slot | null;
  onClose: () => void;
}

const SlotRankModal: React.FC<Props> = ({ visible, slot, onClose }) => {
  const [showGraph, setShowGraph] = useState(false);
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);

  const renderRankDot = useCallback((props: any) => {
    const { payload } = props;
    const isOutside = payload.rank === null || payload.rank === undefined || payload.rank > 500;
    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={isOutside ? 6 : 4}
        fill="#1890ff"
        stroke="#1890ff"
        strokeWidth={isOutside ? 3 : 2}
      />
    );
  }, []);

  // 모달 클릭 시 체감 개선:
  // - 모달은 즉시 열고
  // - rank_history는 모달 오픈 시에만 서버에서 조회하고(스토어 저장 X), 로컬 state에 보관
  const [prepared, setPrepared] = useState(false);
  const [rankData, setRankData] = useState<{ [date: string]: number | null }>({});
  const [sortedDates, setSortedDates] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const jobRef = useRef<number | null>(null);
  const reqSeqRef = useRef(0);

  useEffect(() => {
    if (!visible || !slot) return;

    // 초기화
    setPrepared(false);
    setSortedDates([]);
    setRankData({});
    setShowGraph(false);
    setLoadError(null);

    // 이전 작업 취소
    if (jobRef.current != null) {
      if (typeof (window as any).cancelIdleCallback === 'function') (window as any).cancelIdleCallback(jobRef.current);
      else window.clearTimeout(jobRef.current);
      jobRef.current = null;
    }

    const seq = ++reqSeqRef.current;
    const run = async () => {
      try {
        if (!me) throw new Error('로그인이 필요합니다.');
        const nextRankData = await dispatch(fetchSlotRankHistory({ me, slotId: slot.slotId })).unwrap();
        if (seq !== reqSeqRef.current) return; // stale
        const nextSorted = Object.keys(nextRankData).sort();
        setRankData(nextRankData);
        setSortedDates(nextSorted);
        setPrepared(true);
      } catch (e: any) {
        if (seq !== reqSeqRef.current) return;
        setLoadError(e?.message ?? '순위 이력을 불러오지 못했습니다.');
        setPrepared(true);
      }
    };

    if (typeof (window as any).requestIdleCallback === 'function') {
      jobRef.current = (window as any).requestIdleCallback(run, { timeout: 200 });
    } else {
      jobRef.current = window.setTimeout(() => { void run(); }, 0);
    }

    return () => {
      if (jobRef.current != null) {
        if (typeof (window as any).cancelIdleCallback === 'function') (window as any).cancelIdleCallback(jobRef.current);
        else window.clearTimeout(jobRef.current);
        jobRef.current = null;
      }
    };
  }, [visible, slot, dispatch, me]);

  const hasData = prepared && !loadError && sortedDates.length > 0;

  const dataRange = useMemo(() => {
    if (!hasData) return '';
    const startDate = dayjs(sortedDates[0]).format('M월 D일');
    const endDate = dayjs(sortedDates[sortedDates.length - 1]).format('M월 D일');
    return `${startDate} ~ ${endDate}`;
  }, [hasData, sortedDates]);

  // 순위 통계 계산 메모이제이션
  const { initialRank, latestRank, rankChangeText, rankChangeColor } = useMemo(() => {
    if (!hasData) {
      return { 
        initialRank: null, 
        latestRank: null, 
        rankChangeText: '변화 없음', 
        rankChangeColor: '#666' 
      };
    }

    // 첫 순위와 마지막 순위 계산 (501은 500위 밖으로 간주)
    const initialRank = rankData[sortedDates[0]];
    const latestRank = rankData[sortedDates[sortedDates.length - 1]];
    
    // 순위 변화 계산 (500위 초과는 500위 밖)
    let rankChangeText = '변화 없음';
    let rankChangeColor = '#666';
    
    const initOutside = initialRank === null || initialRank === undefined || initialRank > 500;
    const latestOutside = latestRank === null || latestRank === undefined || latestRank > 500;

    if (initOutside && latestOutside) {
      rankChangeText = '변화 없음';
      rankChangeColor = '#666';
    } else if (initOutside && !latestOutside) {
      rankChangeText = '↑ 상승';
      rankChangeColor = '#ff4d4f';
    } else if (!initOutside && latestOutside) {
      rankChangeText = '↓ 하락';
      rankChangeColor = '#1890ff';
    } else if (!initOutside && !latestOutside) {
      const rankChange = (initialRank as number) - (latestRank as number);
      if (rankChange > 0) {
        rankChangeText = `↑ ${rankChange}위`;
        rankChangeColor = '#ff4d4f';
      } else if (rankChange < 0) {
        rankChangeText = `↓ ${Math.abs(rankChange)}위`;
        rankChangeColor = '#1890ff';
      } else {
        rankChangeText = '변화 없음';
        rankChangeColor = '#666';
      }
    }
    
    return { initialRank, latestRank, rankChangeText, rankChangeColor };
  }, [rankData, sortedDates, hasData]);

  // 그래프는 탭을 눌렀을 때만 계산/렌더 (초기 모달 오픈 속도 개선)
  const axisMax = useMemo(() => {
    if (!showGraph || !hasData) return 10;
    const validRanks = sortedDates
      .map(d => rankData[d])
      .filter((v): v is number => typeof v === 'number' && v <= 500);
    if (validRanks.length === 0) return 10;
    const maxRank = Math.max(...validRanks);
    const rounded = Math.ceil(maxRank / 10) * 10;
    return Math.max(10, rounded);
  }, [showGraph, sortedDates, rankData, hasData]);

  const chartData = useMemo(() => {
    if (!showGraph || !hasData) return [];
    return sortedDates.map(date => {
      const r = rankData[date];
      const outside = r === null || r === undefined || r > 500;
      return {
        date: dayjs(date).format('MM/DD'),
        rank: r ?? null,
        fullDate: date,
        rankForChart: outside ? axisMax : (r as number),
      };
    });
  }, [showGraph, sortedDates, rankData, hasData, axisMax]);

  // 테이블용 데이터 생성
  const tableData = useMemo(() => {
    if (!hasData) return [];
    
    const rows = sortedDates.map((date, index) => {
      const currentRank = rankData[date];
      const prevRank = index > 0 ? rankData[sortedDates[index - 1]] : null;
      
      // 순위 변화 계산 (500위 초과는 500위 밖)
      let changeText = '-';
      if (index > 0) {
        const prevOutside = prevRank === null || prevRank === undefined || prevRank > 500;
        const currOutside = currentRank === null || currentRank === undefined || currentRank > 500;
        if (prevOutside && currOutside) {
          changeText = '변화 없음';
        } else if (prevOutside && !currOutside) {
          changeText = '↑ 상승';
        } else if (!prevOutside && currOutside) {
          changeText = '↓ 하락';
        } else if (!prevOutside && !currOutside) {
          const change = (prevRank as number) - (currentRank as number);
          if (change > 0) {
            changeText = `↑ ${change}위`;
          } else if (change < 0) {
            changeText = `↓ ${Math.abs(change)}위`;
          } else {
            changeText = '변화 없음';
          }
        }
      }
      
      return {
        key: index,
        displayDate: dayjs(date).format('YYYY년 MM월 DD일'),
        // 500위 초과는 500위 밖으로 표기
        rankText: (currentRank === null || currentRank === undefined || currentRank > 500) ? '500위 밖' : `${currentRank}위`,
        rankValue: currentRank,
        change: changeText,
      };
    });

    // 최신 날짜가 위로 오도록 역순 정렬
    return rows.reverse();
  }, [sortedDates, rankData, hasData]);

  // 표 컬럼 정의
  const columns = [
    {
      title: '날짜',
      dataIndex: 'displayDate',
      key: 'date',
      width: 120,
    },
    {
      title: '순위',
      dataIndex: 'rankText',
      key: 'rank',
      width: 100,
    },
    {
      title: '변화',
      dataIndex: 'change',
      key: 'change',
      width: 120,
      render: (text: string) => {
        if (!text || text === '-') return <span style={{ color: '#999' }}>{text || '-'}</span>;
        if (text.includes('↑')) return <span style={{ color: '#ff4d4f' }}>{text}</span>;
        if (text.includes('↓')) return <span style={{ color: '#1890ff' }}>{text}</span>;
        return <span style={{ color: '#666' }}>{text}</span>;
      }
    }
  ];

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            슬롯 {slot?.slotId ?? ''} 순위 이력
          </Title>
          {dataRange && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              데이터 범위: {dataRange}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={420}
      destroyOnClose
    >
      {!slot ? (
        <Empty description="슬롯을 선택해주세요." style={{ padding: '40px 0' }} />
      ) : !prepared ? (
        <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
          <Spin tip="순위 이력을 불러오는 중..." />
        </div>
      ) : loadError ? (
        <Empty description={loadError} style={{ padding: '40px 0' }} />
      ) : !hasData ? (
        <Empty
          description="아직 순위 데이터가 없습니다."
          style={{ padding: '40px 0' }}
        />
      ) : (
        <>
      {/* 순위 요약 카드 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="키워드"
              value={slot.main_keyword}
              valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
            />
          </Col>
          <Col span={6}>
              <Statistic
                title="금일 순위"
                value={(() => {
                  const v = (slot.today_rank ?? latestRank) as any;
                  return (v === null || v === undefined || v > 500) ? '500위 밖' : v;
                })()}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
          </Col>
          <Col span={6}>
              <Statistic
                title="시작 순위"
                value={(initialRank === null || initialRank === undefined || initialRank > 500) ? '500위 밖' : initialRank}
                valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
              />
          </Col>
          <Col span={6}>
            <Statistic
              title="시작일 대비"
              value={rankChangeText}
              valueStyle={{ fontSize: '16px', fontWeight: 'bold', color: rankChangeColor }}
            />
          </Col>
        </Row>
      </Card>

      {/* 표/그래프 토글 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
          <Space.Compact style={{ background: '#f0f2f5', borderRadius: '8px', padding: '4px' }}>
            <Button
              type={showGraph ? 'default' : 'primary'}
              icon={<TableOutlined />}
              onClick={() => setShowGraph(false)}
              style={{
                borderRadius: '6px',
                border: 'none',
                boxShadow: showGraph ? 'none' : '0 2px 4px rgba(24, 144, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
            >
              표
            </Button>
            <Button
              type={showGraph ? 'primary' : 'default'}
              icon={<BarChartOutlined />}
              onClick={() => setShowGraph(true)}
              style={{
                borderRadius: '6px',
                border: 'none',
                boxShadow: showGraph ? '0 2px 4px rgba(24, 144, 255, 0.2)' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              그래프
            </Button>
          </Space.Compact>
        </div>

        {showGraph ? (
          /* 순위 그래프 */
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                reversed={true} // 1위가 맨 위로 오도록
                domain={[1, axisMax]} // 보유한 순위 범위에 맞춰 동적 설정
                allowDecimals={false}
                width={30}
              />
              <Tooltip 
                formatter={(value: any, name: string, props: any) => {
                  const originalRank = props.payload.rank;
                  const outside = originalRank === null || originalRank === undefined || originalRank > 500;
                  return [outside ? '500위 밖' : `${originalRank}위`, '순위'];
                }}
                labelFormatter={(label) => `날짜: ${label}`}
              />
              <Line 
                type="monotone" 
                dataKey="rankForChart" 
                stroke="#1890ff" 
                strokeWidth={2}
                dot={renderRankDot}
                activeDot={{ r: 6 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          /* 순위 표 */
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={{ pageSize: 50, showSizeChanger: false, hideOnSinglePage: true }}
            size="small"
            scroll={{ y: 200 }}
          />
        )}
      </Card>
        </>
      )}
    </Modal>
  );
};

export default React.memo(SlotRankModal); 